"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  DEFAULT_EVENTS_FILTERS,
  type EventsFilterState,
  parseFiltersFromQuery,
  serializeFiltersToQuery,
} from "@/utils/eventsFilters";

const GruvHeader = dynamic(() => import("./GruvHeader").then((mod) => mod.GruvHeader));
const GruvFooter = dynamic(() => import("./GruvFooter").then((mod) => mod.GruvFooter));
const ParticleCanvas = dynamic(() => import("../welcome/ParticleCanvas").then((mod) => mod.ParticleCanvas), {
  loading: () => null,
  ssr: false,
});

type GruvShellContextValue = {
  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  filters: EventsFilterState;
  applyFilters: (filters: EventsFilterState) => void;
  replaceFilters: (filters: EventsFilterState) => void;
  resetFilters: () => void;
  /** Provinces with listable events (SSR via EVENTS_URL); overlay may refresh via /api/... */
  eventProvinceOptions: string[];
};

const GruvShellContext = createContext<GruvShellContextValue | undefined>(undefined);

export const useGruvShell = (): GruvShellContextValue => {
  const context = useContext(GruvShellContext);
  if (!context) {
    throw new Error("useGruvShell must be used within a GruvShell provider");
  }
  return context;
};

export interface GruvShellProps {
  children: React.ReactNode;
  searchOverlay?: React.ReactNode;
  initialFilters?: EventsFilterState;
  initialEventProvinces?: string[];
}

export function GruvShell({ children, searchOverlay, initialFilters, initialEventProvinces = [] }: GruvShellProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filters, setFilters] = useState<EventsFilterState>(initialFilters ?? DEFAULT_EVENTS_FILTERS);

  /* Route-based styling: full-immersive home, blur on secondary pages */
  const isHome = pathname === "/";
  const isUsersRoute = pathname === "/users" || pathname.startsWith("/users/");
  const shouldRenderParticles = isHome || isUsersRoute;
  const shouldBlur = !isHome && shouldRenderParticles;
  const shouldUseStaticBackground = !shouldRenderParticles;

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  const syncUrl = useCallback((next: EventsFilterState) => {
    if (typeof window === "undefined") {
      return;
    }
    const query = serializeFiltersToQuery(next);
    const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState({}, "", url);
  }, []);

  const applyFilters = useCallback(
    (nextFilters: EventsFilterState) => {
      setFilters(nextFilters);
      setIsSearchOpen(false);

      // Always navigate to /events with the filter query params
      const query = serializeFiltersToQuery(nextFilters);
      const url = query ? `/events?${query}` : '/events';
      router.push(url);
    },
    [router],
  );

  const replaceFilters = useCallback(
    (nextFilters: EventsFilterState) => {
      setFilters(nextFilters);
      syncUrl(nextFilters);
    },
    [syncUrl],
  );

  const resetFilters = useCallback(() => {
    applyFilters(DEFAULT_EVENTS_FILTERS);
  }, [applyFilters]);

  // Hydrate filters from URL on mount
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const parsed = parseFiltersFromQuery(params);
    setFilters((current) => {
      const hasChanged = JSON.stringify(current) !== JSON.stringify(parsed);
      if (!hasChanged) {
        return current;
      }
      return parsed;
    });
  }, []);

  // Keep filters in sync with URL when navigating (e.g. /events?date=today)
  useEffect(() => {
    if (pathname !== "/events") {
      return;
    }
    const parsed = parseFiltersFromQuery(searchParams);
    setFilters((current) => {
      const hasChanged = JSON.stringify(current) !== JSON.stringify(parsed);
      if (!hasChanged) {
        return current;
      }
      return parsed;
    });
  }, [pathname, searchParams]);

  const value = useMemo<GruvShellContextValue>(
    () => ({
      isSearchOpen,
      openSearch,
      closeSearch,
      filters,
      applyFilters,
      replaceFilters,
      resetFilters,
      eventProvinceOptions: initialEventProvinces,
    }),
    [
      isSearchOpen,
      openSearch,
      closeSearch,
      filters,
      applyFilters,
      replaceFilters,
      resetFilters,
      initialEventProvinces,
    ],
  );

  return (
    <GruvShellContext.Provider value={value}>
      <div className={[
        "gruv-shell",
        isHome && "gruv-shell--home",
        isUsersRoute && "gruv-shell--users",
        shouldBlur && "gruv-shell--blurred",
        shouldUseStaticBackground && "gruv-shell--static-bg",
      ].filter(Boolean).join(" ")}>
        {shouldRenderParticles && <ParticleCanvas isHome={isHome} />}
        {!isHome && <GruvHeader />}
        <div className="gruv-shell__main">{children}</div>
        {!isHome && <GruvFooter />}
      </div>
      {searchOverlay}
    </GruvShellContext.Provider>
  );
}

